// din-ndis-quote-form.js
// <ndis-nav>                 — tab switcher; each button names its panel via data-ndis-nav-panel.
// <ndis-quote-form>          — NDIS enquiry form. Owns: management-type branching, quantity
//                              steppers, live validation, and the reCAPTCHA v3 + APIEase submit.
//                              Each management type maps to its own APIEase flow (see FLOWS).
// <ndis-estimate-calculator> — standalone co-payment/contribution estimator. On submit it
//                              broadcasts ESTIMATE_EVENT so the quote form can prefill itself.
// Copy lives in Liquid; config arrives via data-* attributes on the hosts.

import { isValidEmail, isValidAusPhone, melbourneTime, scrollToId, apiEaseFetcherWithRecaptcha } from "@din/utilities";

const ANALYTICS_FORM_ID = "ndis-form";

// Dispatched on document by <ndis-estimate-calculator>, consumed by <ndis-quote-form>.
const ESTIMATE_EVENT = "ndis:estimate";

const SECONDS_PER_ORDER = {
  "every-week": 604800,
  "every-2-weeks": 1209600,
  "every-4-weeks": 2419200
};

const FIELD_LABELS = {
  firstName: "First Name",
  lastName: "Last Name",
  email: "Email Address",
  phone: "Phone Number",
  ndisNumber: "NDIS Number",
  planEndDate: "NDIS Plan End Date",
  readyMeals: "Number of Ready Meals",
  mealsForTwo: "Number of Meals for Two",
  soups: "Number of Soups",
  desserts: "Number of Desserts",
  frequency: "Order Frequency",
  planContact: "Your Agency Contact",
  planEmail: "Email Address",
  agencyEmail: "Email Address"
};

const MEAL_FIELDS = ["readyMeals", "mealsForTwo", "soups", "desserts"];
const PLAN_FIELDS = ["planEndDate", ...MEAL_FIELDS, "frequency"];

// One entry per management type: which APIEase action to call (ids in din-const.js), which
// fields are required, which are only validated when filled in, and which are sent.
const FLOWS = {
  "plan-managed": {
    action: "ndisPlanManaged",
    required: ["email", "firstName", "lastName", "ndisNumber", ...PLAN_FIELDS, "planContact", "planEmail"],
    optional: ["phone"],
    payload: ["firstName", "lastName", "email", "phone", "ndisNumber", ...PLAN_FIELDS, "quote", "planContact", "planEmail"],
    waitCopy: "submitting"
  },
  "self-managed": {
    action: "ndisSelfManaged",
    required: ["email", "firstName", "lastName", "ndisNumber"],
    optional: [],
    payload: ["firstName", "lastName", "email", "ndisNumber"],
    waitCopy: "self-managed"
  },
  "agency-managed": {
    action: "ndisAgencyManaged",
    required: ["email", "firstName", "lastName", "ndisNumber", ...PLAN_FIELDS],
    optional: ["phone", "agencyEmail"],
    payload: ["firstName", "lastName", "email", "phone", "ndisNumber", ...PLAN_FIELDS, "quote", "agencyEmail"],
    waitCopy: "submitting"
  }
};

// Sent when no Support Coordinator address is supplied, so the flow still has a recipient.
const UNPROVIDED_AGENCY_EMAIL = "not-provided@dineamic.com.au";

const NDIS_NUMBER_PATTERN = /^[456]\d{8}$/;

const EMAIL_FIELDS = new Set(["email", "planEmail", "agencyEmail"]);

// en-CA formats as YYYY-MM-DD, which is what both date inputs expect.
const melbourneDate = (date) =>
  date.toLocaleDateString("en-CA", {
    timeZone: "Australia/Melbourne",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });

// Whole billing cycles left before the plan ends — drives the quote's order count.
const ordersUntil = (endDate, frequency) => {
  const seconds = SECONDS_PER_ORDER[frequency] || SECONDS_PER_ORDER["every-4-weeks"];
  const msRemaining = new Date(endDate).getTime() - Date.now();
  if (!Number.isFinite(msRemaining)) return 0;
  return Math.floor(msRemaining / 1000 / seconds);
};

const weeksUntil = (endDate) => {
  const msRemaining = new Date(endDate).getTime() - Date.now();
  if (!Number.isFinite(msRemaining)) return 0;
  return Math.floor(msRemaining / (1000 * 60 * 60 * 24 * 7));
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(amount);

class NdisNav extends HTMLElement {
  constructor() {
    super();

    this.isInitialized = false;
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.cacheElements();
    if (!this.buttons.length || this.isInitialized) return;

    this.addEventListener("click", this.handleClick);
    this.activateDefault();
    this.isInitialized = true;
  }

  disconnectedCallback() {
    if (!this.isInitialized) return;

    this.removeEventListener("click", this.handleClick);
    this.isInitialized = false;
  }

  // Panels live outside the nav (they are separate sections), so they stay a document lookup.
  cacheElements() {
    this.buttons = Array.from(this.querySelectorAll("[data-ndis-nav-target]"));
    this.panels = {};
    this.buttons.forEach((button) => {
      const panel = button.dataset.ndisNavPanel && document.querySelector(button.dataset.ndisNavPanel);
      if (panel) this.panels[button.dataset.ndisNavTarget] = panel;
    });
  }

  handleClick(event) {
    const button = event.target.closest("[data-ndis-nav-target]");
    if (!button || !this.contains(button)) return;

    const target = button.dataset.ndisNavTarget;
    if (!target || !this.panels[target]) return;

    event.preventDefault();
    this.activate(target);
  }

  activate(target) {
    this.buttons.forEach((button) => {
      button.classList.toggle("selected", button.dataset.ndisNavTarget === target);
    });
    Object.entries(this.panels).forEach(([key, panel]) => {
      panel.style.display = key === target ? "block" : "none";
    });
  }

  activateDefault() {
    const defaultButton = this.querySelector("[data-ndis-nav-default]") || this.buttons[0];
    const target = defaultButton && defaultButton.dataset.ndisNavTarget;
    if (target && this.panels[target]) this.activate(target);
  }
}

if (!customElements.get("ndis-nav")) {
  customElements.define("ndis-nav", NdisNav);
}

class NdisQuoteForm extends HTMLElement {
  constructor() {
    super();

    this.isInitialized = false;
    this.isSubmitting = false;
    this.managementType = "";
    this.frequency = "";
    this.planContact = "";
    this.leadPushed = false;

    this.handleClick = this.handleClick.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleEstimate = this.handleEstimate.bind(this);
  }

  connectedCallback() {
    this.cacheElements();
    if (!this.form || this.isInitialized) return;

    this.readConfig();
    this.initDatePicker();

    this.addEventListener("click", this.handleClick);
    this.addEventListener("input", this.handleInput);
    this.form.addEventListener("submit", this.handleSubmit);
    document.addEventListener(ESTIMATE_EVENT, this.handleEstimate);
    this.isInitialized = true;
  }

  disconnectedCallback() {
    if (!this.isInitialized) return;

    this.removeEventListener("click", this.handleClick);
    this.removeEventListener("input", this.handleInput);
    this.form.removeEventListener("submit", this.handleSubmit);
    document.removeEventListener(ESTIMATE_EVENT, this.handleEstimate);
    this.isInitialized = false;
  }

  cacheElements() {
    this.form = this.querySelector("#din-cv2nf");
    this.submitButton = this.querySelector(".din-ndis-v2-ndis-submit");
    this.honeypot = this.querySelector("#din-ndis-v2-website");

    this.typeSelector = this.querySelector("#din-ndis-v2-ndis-type");
    this.frequencySelector = this.querySelector("#din-ndis-v2-frequency");
    this.contactSelector = this.querySelector("#din-ndis-v2-contact-type");
    this.conditionalBlocks = Array.from(this.querySelectorAll(".din-ndis-show-hide"));
    this.selfManagedPathway = this.querySelector("#din-custom-v2-sm-pathway-p");
    this.assistedPathway = this.querySelector("#din-custom-v2-pm-am-pathway-p");

    this.errorWrappers = Array.from(this.querySelectorAll(".din-ndis-v2-error-wrapper"));
    this.errorHeading = this.querySelector("#din-ndis-v2-error-heading");
    this.errorList = this.querySelector("#din-ndis-v2-error-list");

    this.formPanel = this.querySelector(".din-custom-v2-ndis-wrapper");
    this.waitPanel = this.querySelector(".din-custom-v2-ndis-wrapper-wait");
    this.waitCopyEl = this.querySelector("#din-ndis-v2-wait-copy");
    this.resultPanels = {};
    this.querySelectorAll("[data-ndis-result]").forEach((panel) => {
      this.resultPanels[panel.dataset.ndisResult] = panel;
    });
    this.selfManagedEmailEl = this.querySelector("#dinV2SmNdisEmailSubmitted");
    this.quoteEmailsEl = this.querySelector("[data-ndis-result-emails]");

    this.inputs = {
      firstName: this.querySelector("#din-ndis-v2-first-name"),
      lastName: this.querySelector("#din-ndis-v2-last-name"),
      email: this.querySelector("#din-ndis-v2-email-address"),
      phone: this.querySelector("#din-ndis-v2-phone"),
      ndisNumber: this.querySelector("#din-ndis-v2-ndis-number"),
      planEndDate: this.querySelector("#din-ndis-v2-end-date-flatpickr"),
      readyMeals: this.querySelector("#din-ndis-v2-ready-meals"),
      mealsForTwo: this.querySelector("#din-ndis-v2-meals-for-two"),
      soups: this.querySelector("#din-ndis-v2-soups"),
      desserts: this.querySelector("#din-ndis-v2-desserts"),
      planEmail: this.querySelector("#din-ndis-v2-email-address-plan"),
      agencyEmail: this.querySelector("#din-ndis-v2-email-address-agency")
    };
    // The two button groups have no input of their own; the selector wrapper carries the error state.
    this.errorTargets = { frequency: this.frequencySelector, planContact: this.contactSelector };

    // Shared with the delivery-check section, so it stays an explicit document-level dependency.
    this.deliveryScheduleWrapper = document.querySelector(".delivery-schedule__wrapper");
  }

  readConfig() {
    this.anchorId = (this.dataset.anchorId || "").trim();
    this.waitCopy = {
      submitting: this.dataset.waitCopySubmitting || "",
      "self-managed": this.dataset.waitCopySelfManaged || ""
    };
    this.planMonths = Number(this.dataset.defaultPlanMonths) || 3;
  }

  // flatpickr is a page-level dependency loaded from theme settings; fall back to a native
  // date input (same Y-m-d value shape) when the page isn't configured to include it.
  initDatePicker() {
    const input = this.inputs.planEndDate;
    if (!input) return;

    const defaultDate = new Date();
    defaultDate.setMonth(defaultDate.getMonth() + this.planMonths);

    if (typeof window.flatpickr !== "function") {
      input.type = "date";
      input.min = melbourneDate(new Date());
      input.value = melbourneDate(defaultDate);
      return;
    }

    window.flatpickr(input, {
      dateFormat: "Y-m-d",
      altFormat: "d/m/Y",
      altInput: true,
      defaultDate,
      disableMobile: true,
      minDate: new Date(),
      allowInput: true
    });
  }

  /* ---------- events ---------- */

  handleClick(event) {
    if (event.target.closest("#open-deli-schedule-div-for-ndis")) {
      event.preventDefault();
      if (this.deliveryScheduleWrapper) this.deliveryScheduleWrapper.classList.add("active");
      return;
    }

    if (event.target.closest("[data-ndis-retry]")) {
      event.preventDefault();
      this.showPanel("form");
      return;
    }

    const typeOption = event.target.closest(".din-custom-v2-ndis-option");
    if (typeOption) {
      event.preventDefault();
      this.selectManagementType(typeOption.dataset.option || "");
      return;
    }

    const frequencyOption = event.target.closest(".din-custom-v2-ndis-frequency-option");
    if (frequencyOption) {
      event.preventDefault();
      this.selectFrequency(frequencyOption.dataset.option || "");
      return;
    }

    const contactOption = event.target.closest(".din-custom-v2-ndis-contact-option");
    if (contactOption) {
      event.preventDefault();
      this.selectPlanContact(contactOption.dataset.option || "");
      return;
    }

    const stepper = event.target.closest(".din-v2-qty-minus, .din-v2-qty-plus");
    if (stepper) {
      event.preventDefault();
      const input = this.querySelector(`#${stepper.dataset.target}`);
      if (!input) return;
      const step = stepper.classList.contains("din-v2-qty-plus") ? 1 : -1;
      input.value = String(Math.max(0, (parseInt(input.value, 10) || 0) + step));
    }
  }

  handleInput(event) {
    const input = event.target.closest(".din-ndis-v2-input-value, .din-v2-qty-selector input");
    if (!input) return;

    if (input.closest(".din-v2-qty-selector")) {
      const value = parseInt(input.value, 10);
      input.value = String(Number.isFinite(value) && value > 0 ? value : 0);
      return;
    }

    const value = input.value.trim();
    if (input.type === "email") {
      this.setValidity(input, isValidEmail(value));
      return;
    }
    if (input.type === "tel") {
      this.setValidity(input, isValidAusPhone(value));
      return;
    }
    if (input === this.inputs.ndisNumber) {
      this.setValidity(input, NDIS_NUMBER_PATTERN.test(value));
      return;
    }
    this.markValid(input);
  }

  // Prefill from <ndis-estimate-calculator> so a visitor doesn't re-enter what they just modelled.
  handleEstimate(event) {
    const detail = event.detail || {};
    MEAL_FIELDS.forEach((field) => {
      if (this.inputs[field] && detail[field] != null) this.inputs[field].value = String(detail[field]);
    });
    if (detail.frequency) this.selectFrequency(detail.frequency);
  }

  /* ---------- selections ---------- */

  selectManagementType(type) {
    if (!FLOWS[type]) return;
    this.managementType = type;
    this.setSelected(this.typeSelector, ".din-custom-v2-ndis-option", type);

    const selfManaged = type === "self-managed";
    if (this.selfManagedPathway) this.selfManagedPathway.style.display = selfManaged ? "block" : "none";
    if (this.assistedPathway) this.assistedPathway.style.display = selfManaged ? "none" : "block";

    this.conditionalBlocks.forEach((block) => {
      const shown = (block.getAttribute("din-ndis-show") || "").split(",").map((entry) => entry.trim());
      block.classList.toggle("hidden", !shown.includes(type));
    });

    if (this.submitButton) {
      this.submitButton.classList.remove("not-ready");
      this.submitButton.removeAttribute("aria-disabled");
    }
  }

  selectFrequency(frequency) {
    if (!SECONDS_PER_ORDER[frequency]) return;
    this.frequency = frequency;
    this.setSelected(this.frequencySelector, ".din-custom-v2-ndis-frequency-option", frequency);
    this.markValid(this.frequencySelector);
  }

  selectPlanContact(option) {
    this.planContact = option === "support-coordinator" ? "Support Coordinator" : "Plan Manager";
    this.setSelected(this.contactSelector, ".din-custom-v2-ndis-contact-option", option);
    this.markValid(this.contactSelector);
  }

  setSelected(container, optionSelector, value) {
    if (!container) return;
    container.querySelectorAll(optionSelector).forEach((option) => {
      option.classList.toggle("selected", option.dataset.option === value);
    });
  }

  /* ---------- form data & validation ---------- */

  readFormData() {
    const value = (key) => (this.inputs[key] ? this.inputs[key].value.trim() : "");
    return {
      firstName: value("firstName"),
      lastName: value("lastName"),
      email: value("email"),
      phone: value("phone"),
      ndisNumber: value("ndisNumber"),
      planEndDate: value("planEndDate"),
      readyMeals: value("readyMeals"),
      mealsForTwo: value("mealsForTwo"),
      soups: value("soups"),
      desserts: value("desserts"),
      frequency: this.frequency,
      planContact: this.planContact,
      planEmail: value("planEmail"),
      // Agency-managed leads without a coordinator address still need a routable recipient.
      agencyEmail: value("agencyEmail") || UNPROVIDED_AGENCY_EMAIL,
      // Only the plan-managed flow always needs a quote; the others leave it unset.
      quote: this.managementType === "plan-managed" ? true : undefined,
      website: this.honeypot ? this.honeypot.value.trim() : ""
    };
  }

  collectErrors(flow, data) {
    const errors = [];
    flow.required.forEach((field) => {
      const message = this.fieldError(field, data[field]);
      if (message) errors.push({ field, message });
    });
    // Optional fields only have to be well-formed when the visitor filled them in.
    flow.optional.forEach((field) => {
      const raw = field === "agencyEmail" && data[field] === UNPROVIDED_AGENCY_EMAIL ? "" : data[field];
      if (!String(raw || "").trim()) return;
      const message = this.fieldError(field, raw);
      if (message) errors.push({ field, message });
    });
    return errors;
  }

  // Returns "" when valid, otherwise the line to show in the error summary.
  fieldError(field, value) {
    const label = FIELD_LABELS[field];
    const trimmed = String(value == null ? "" : value).trim();
    if (!trimmed) return label;
    if (EMAIL_FIELDS.has(field)) return isValidEmail(trimmed) ? "" : `${label} is not formatted properly.`;
    if (field === "phone") return isValidAusPhone(trimmed) ? "" : `${label} is not formatted properly.`;
    if (field === "ndisNumber") {
      return NDIS_NUMBER_PATTERN.test(trimmed) ? "" : "Your NDIS number must be 9 digits long and start with 4, 5 or 6.";
    }
    return "";
  }

  showErrors(errors) {
    if (this.errorList) {
      this.errorList.replaceChildren(
        ...errors.map(({ message }) => {
          const item = document.createElement("li");
          item.textContent = message;
          return item;
        })
      );
    }
    errors.forEach(({ field }) => this.markInvalid(this.errorTargets[field] || this.inputs[field]));

    if (this.errorHeading && !this.errorHeading.textContent) {
      this.errorHeading.textContent = "Please provide the following information and try again.";
    }
    this.errorWrappers.forEach((wrapper) => wrapper.classList.add("is-visible"));
  }

  clearValidation() {
    if (this.errorList) this.errorList.replaceChildren();
    this.errorWrappers.forEach((wrapper) => wrapper.classList.remove("is-visible"));
    Object.values(this.inputs).forEach((input) => this.markValid(input));
    Object.values(this.errorTargets).forEach((target) => this.markValid(target));
  }

  setValidity(input, isValid) {
    if (isValid) this.markValid(input);
    else this.markInvalid(input);
  }

  // flatpickr hides the real input behind a generated alt input, so style whichever is visible.
  visibleField(element) {
    if (!element) return null;
    return (element._flatpickr && element._flatpickr.altInput) || element;
  }

  markInvalid(element) {
    const target = this.visibleField(element);
    if (target) target.classList.add("is-invalid");
  }

  markValid(element) {
    const target = this.visibleField(element);
    if (target) target.classList.remove("is-invalid");
  }

  // Analyzify lead event, fired at most once per form instance.
  pushGenerateLead() {
    if (this.leadPushed) return;
    if (!window.analyzify || typeof window.analyzify.dispatchEvent !== "function") return;
    this.leadPushed = true;
    window.analyzify.dispatchEvent("shopify:form_submitted", {
      form_type: "contact",
      form_id: ANALYTICS_FORM_ID
    });
  }

  /* ---------- submit ---------- */

  handleSubmit(event) {
    event.preventDefault();
    if (this.isSubmitting) return;
    if (this.submitButton && this.submitButton.classList.contains("not-ready")) return;

    const flow = FLOWS[this.managementType];
    if (!flow) return;

    const data = this.readFormData();
    this.clearValidation();

    // Honeypot bot guard — silently stop without tipping the bot off.
    if (data.website) return;

    const errors = this.collectErrors(flow, data);
    if (errors.length) {
      this.showErrors(errors);
      return;
    }

    this.submitForm(flow, data);
  }

  buildPayload(flow, data, token) {
    const payload = {};
    flow.payload.forEach((field) => {
      if (data[field] !== undefined) payload[field] = data[field];
    });
    payload.recaptchaToken = token;
    payload.melbTime = melbourneTime();
    if (flow.payload.includes("planEndDate")) {
      payload.remainingWeeks = weeksUntil(data.planEndDate);
      payload.numOfOrders = ordersUntil(data.planEndDate, data.frequency);
    }
    return payload;
  }

  submitForm(flow, data) {
    if (!window.grecaptcha) {
      this.showPanel("failure");
      return;
    }

    this.isSubmitting = true;
    if (this.submitButton) this.submitButton.disabled = true;
    this.showWait(flow);

    this.pushGenerateLead();

    apiEaseFetcherWithRecaptcha(flow.action, (token) => this.buildPayload(flow, data, token))
      .then((response) => this.handleSubmissionResponse(response, data))
      .catch(() => this.showPanel("failure"))
      .finally(() => {
        this.isSubmitting = false;
        if (this.submitButton) this.submitButton.disabled = false;
      });
  }

  // `response` is the APIEase body's `data`.
  handleSubmissionResponse(response, data) {
    const status = response && response.googleRecaptureStatus;
    if (status === "failure") {
      this.showPanel("bot");
      return;
    }
    if (status !== "success") {
      this.showPanel("failure");
      return;
    }

    const confirmed = response.requestFlowParameters || {};
    const email = confirmed.email || data.email;

    if (this.managementType === "self-managed") {
      if (this.selfManagedEmailEl) this.selfManagedEmailEl.textContent = email;
      this.showPanel("self-managed");
      return;
    }

    // Plan-managed leads whose nominated contact is a Support Coordinator get the same
    // confirmation as agency-managed: both recipients are named.
    const secondEmail =
      this.managementType === "plan-managed"
        ? (data.planContact === "Support Coordinator" ? confirmed.planEmail || data.planEmail : "")
        : data.agencyEmail;
    const named = secondEmail && secondEmail !== UNPROVIDED_AGENCY_EMAIL ? `${email} and ${secondEmail}` : email;
    if (this.quoteEmailsEl) this.quoteEmailsEl.textContent = named;
    this.showPanel("quote");
  }

  showWait(flow) {
    if (this.waitCopyEl) this.waitCopyEl.textContent = this.waitCopy[flow.waitCopy] || "";
    this.showPanel("wait");
    scrollToId(this.anchorId);
  }

  // One visible panel at a time: the form, the spinner, or one of the result variants.
  showPanel(name) {
    const panels = [this.formPanel, this.waitPanel, ...Object.values(this.resultPanels)];
    panels.forEach((panel) => {
      if (panel) panel.classList.remove("is-active");
    });

    const active = { form: this.formPanel, wait: this.waitPanel }[name] || this.resultPanels[name];
    if (active) active.classList.add("is-active");
  }
}

if (!customElements.get("ndis-quote-form")) {
  customElements.define("ndis-quote-form", NdisQuoteForm);
}

class NdisEstimateCalculator extends HTMLElement {
  constructor() {
    super();

    this.isInitialized = false;
    this.handleClick = this.handleClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  connectedCallback() {
    this.cacheElements();
    if (!this.form || this.isInitialized) return;

    this.setDefaultEndDate();
    this.addEventListener("click", this.handleClick);
    this.form.addEventListener("submit", this.handleSubmit);
    this.isInitialized = true;
  }

  disconnectedCallback() {
    if (!this.isInitialized) return;

    this.removeEventListener("click", this.handleClick);
    this.form.removeEventListener("submit", this.handleSubmit);
    this.isInitialized = false;
  }

  cacheElements() {
    this.form = this.querySelector("#dinNdisEstimateCalc");
    this.endDateInput = this.querySelector("#dinNdisEndDate");
    this.frequencySelect = this.querySelector("#dinOrderingFrequency");
    this.results = this.querySelector("#dinNdisResults");
    this.quantities = {
      readyMeals: this.querySelector("#dinReadyMeals"),
      mealsForTwo: this.querySelector("#dinMealsForTwo"),
      soups: this.querySelector("#dinSoups"),
      desserts: this.querySelector("#dinDesserts")
    };
    this.outputs = {
      copayment: this.querySelector("#dinNdisCopaymentResult"),
      contribution: this.querySelector("#dinNdisContributionResult"),
      orders: this.querySelector("#dinNdisNumOfOrdersResult"),
      copaymentTotal: this.querySelector("#dinNdisCopaymentTotalResult"),
      contributionTotal: this.querySelector("#dinNdisContributionTotalResult")
    };
  }

  setDefaultEndDate() {
    if (!this.endDateInput || this.endDateInput.value) return;
    const nextYear = new Date();
    nextYear.setFullYear(nextYear.getFullYear() + 1);
    this.endDateInput.value = melbourneDate(nextYear);
  }

  handleClick(event) {
    const stepper = event.target.closest(".quantity-btn");
    if (!stepper) return;
    event.preventDefault();
    const input = this.querySelector(`#${stepper.dataset.target}`);
    if (!input) return;
    const step = stepper.dataset.action === "increase" ? 1 : -1;
    input.value = String(Math.max(0, (parseInt(input.value, 10) || 0) + step));
  }

  // Unit prices come from the din_config "din-public-data" metaobject
  // (window.__DIN__.dinPublicData.aveRRP). Fallbacks mirror the current metaobject values.
  unitPrices() {
    const rrp = (window.__DIN__ && window.__DIN__.dinPublicData && window.__DIN__.dinPublicData.aveRRP) || {};
    return {
      readyMeals: rrp.readyMealsRRP ?? 12,
      mealsForTwo: rrp.mealsForTwoRRP ?? 16.95,
      soups: rrp.soupsRRP ?? 8,
      desserts: rrp.dessertsRRP ?? 6.95
    };
  }

  handleSubmit(event) {
    event.preventDefault();

    const counts = {};
    Object.entries(this.quantities).forEach(([key, input]) => {
      counts[key] = input ? parseInt(input.value, 10) || 0 : 0;
    });

    // The calculator and the quote form spell the same frequencies with _ and - respectively.
    const frequency = (this.frequencySelect ? this.frequencySelect.value : "").replace(/_/g, "-");
    const endDate = this.endDateInput ? this.endDateInput.value : "";
    const orders = ordersUntil(endDate, frequency);

    const prices = this.unitPrices();
    const subtotal = Object.keys(counts).reduce((total, key) => total + counts[key] * prices[key], 0);
    const copayment = subtotal * 0.25; // 25% of the subtotal
    const contribution = subtotal * 0.75 + 15; // 75% of the subtotal + the $15 delivery fee

    if (this.outputs.copayment) this.outputs.copayment.textContent = formatCurrency(copayment);
    if (this.outputs.contribution) this.outputs.contribution.textContent = formatCurrency(contribution);
    if (this.outputs.orders) this.outputs.orders.textContent = String(orders);
    if (this.outputs.copaymentTotal) this.outputs.copaymentTotal.textContent = formatCurrency(copayment * orders);
    if (this.outputs.contributionTotal) this.outputs.contributionTotal.textContent = formatCurrency(contribution * orders);
    if (this.results) this.results.hidden = false;

    document.dispatchEvent(new CustomEvent(ESTIMATE_EVENT, { detail: { ...counts, frequency } }));
  }
}

if (!customElements.get("ndis-estimate-calculator")) {
  customElements.define("ndis-estimate-calculator", NdisEstimateCalculator);
}
